/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { VectorMap } from '@south-paw/react-vector-maps';

import brMap from './brasil.json';
import { comparativeTypes } from '../../../../../../redux/dashboard-completed';
import { comparativeUnits } from '../Comparative';

import HeatScale from './components/HeatScale';

import {
  DataBoxContainer,
  Container,
  DataBox,
  DataText,
  MapContainer,
  ObsText,
  ScaleContainer,
  ColumnWrapper,
} from './styles';

const colorScale = {
  TEN: '#F20D0D',
  NINE: '#F96210',
  EIGHT: '#F98A0B',
  SEVEN: '#F7B022',
  SIX: '#F2DF0D',
  FIVE: '#E1F50A',
  FOUR: '#C2F820',
  THREE: '#87F20D',
  TWO: '#60DF20',
  ONE: '#22C322',
};

const brRegions = {
  Sul: ['br-pr', 'br-sc', 'br-rs'],
  Sudeste: ['br-sp', 'br-rj', 'br-es', 'br-mg'],
  CentroOeste: ['br-ms', 'br-mt', 'br-go', 'br-df'],
  Nordeste: [
    'br-ba',
    'br-se',
    'br-al',
    'br-pe',
    'br-pb',
    'br-rn',
    'br-pi',
    'br-ma',
    'br-ce',
  ],
  Norte: ['br-to', 'br-ro', 'br-pa', 'br-ap', 'br-rr', 'br-am', 'br-ac'],
};

const labelTraskey = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
  DF: 'Distrito Federal',
};

const HeatMap = ({
  data,
  comparativeType,
  comparativeUnit,
  activeRegions,
  setActiveRegions,
  activeState,
  setActiveState,
}) => {
  const [hovered, setHovered] = useState('None');
  const [focused, setFocused] = useState('None');
  const [clicked, setClicked] = useState(null);

  const mapData = data || [];

  const newMapData = mapData.map(el => ({
    ...el,
    total: el.onTimeQty + el.lateQty,
    onTimePercentage: (
      (el.onTimeQty / (el.onTimeQty + el.lateQty)) *
      100
    ).toFixed(2),
    latePercentage: ((el.lateQty / (el.onTimeQty + el.lateQty)) * 100).toFixed(
      2,
    ),
  }));

  const _getSelectedState = target =>
    newMapData.find(el => {
      let match;
      if (comparativeType === comparativeTypes.REGION) {
        Object.keys(brRegions).find(key => {
          if (key === el.label) {
            brRegions[key].find(value => {
              if (value === target.attributes.id.value) {
                match = el;
              }
            });
          }
        });
        if (match) {
          setActiveRegions(brRegions[match.label]);
        }
        return match;
      }

      if (target.attributes.id.value === `br-${el.label.toLowerCase()}`) {
        setActiveState(el);
        return el;
      }
    });

  const layerProps = {
    onMouseEnter: ({ target }) => setHovered(target.attributes.name.value),
    onMouseLeave: ({ target }) => setHovered('None'),
    onFocus: ({ target }) => setFocused(target.attributes.name.value),
    onBlur: ({ target }) => {
      setFocused('None');
    },
    onClick: ({ target }) => {
      const selectedTarget = _getSelectedState(target);

      if (selectedTarget) {
        setClicked(selectedTarget);
        return;
      }

      const noDataToShow = {
        label: target.attributes.id.value.slice(-2).toUpperCase(),
        obs: (
          <div>
            <br />
            <div>Ops, parece que não há dados suficientes para exibir.</div>
            <br />
            <div>Tente em outro momento, ou entre em contato com o SAC se isto representa um problema.</div>
          </div>
        )
      };

      setClicked(noDataToShow);
    },
  };

  const _getColorByPercentage = onTimePercentage => {
    const onTimePercentageNumber = Number(onTimePercentage);

    if (onTimePercentageNumber >= 0 && onTimePercentageNumber <= 10) {
      return colorScale.TEN;
    }

    if (onTimePercentageNumber > 10 && onTimePercentageNumber <= 20) {
      return colorScale.NINE;
    }

    if (onTimePercentageNumber > 20 && onTimePercentageNumber <= 30) {
      return colorScale.EIGHT;
    }

    if (onTimePercentageNumber > 30 && onTimePercentageNumber <= 40) {
      return colorScale.SEVEN;
    }

    if (onTimePercentageNumber > 40 && onTimePercentageNumber <= 50) {
      return colorScale.SIX;
    }

    if (onTimePercentageNumber > 50 && onTimePercentageNumber <= 60) {
      return colorScale.FIVE;
    }

    if (onTimePercentageNumber > 60 && onTimePercentageNumber <= 70) {
      return colorScale.FOUR;
    }

    if (onTimePercentageNumber > 70 && onTimePercentageNumber <= 80) {
      return colorScale.THREE;
    }

    if (onTimePercentageNumber > 80 && onTimePercentageNumber <= 90) {
      return colorScale.TWO;
    }

    if (onTimePercentageNumber > 90 && onTimePercentageNumber <= 100) {
      return colorScale.ONE;
    }
  };

  const stateToRegions = region =>
    brRegions[region.label].map(state => ({
      id: state,
      color: _getColorByPercentage(region.onTimePercentage),
      percentage: region.onTimePercentage,
    }));

  const _relateDataToMap = mapData =>
    mapData.map(el => {
      if (comparativeType === comparativeTypes.UF) {
        return {
          id: `br-${el.label.toLowerCase()}`,
          color: _getColorByPercentage(el.onTimePercentage),
          percentage: el.onTimePercentage,
        };
      }

      if (comparativeType === comparativeTypes.REGION) {
        return stateToRegions(el);
      }

      return {
        id: '',
        color: '',
        percentage: '',
      };
    });

  const _getStateColor = () => _relateDataToMap(newMapData);

  const dataValues = clicked && {
    label: labelTraskey[clicked.label],
    total: clicked.total,
    onTimeValue: clicked.onTimeValue,
    onTimeWeight: clicked.onTimeWeight,
    onTimeQty: clicked.onTimeQty,
    onTimePercentage: clicked.onTimePercentage,
    lateValue: clicked.lateValue,
    lateQty: clicked.lateQty,
    latePercentage: clicked.latePercentage,
    lateWeight: clicked.lateWeight,
    obs: clicked.obs,
  };
  
  return (
    <MapContainer
      activeState={activeState}
      activeRegions={activeRegions}
      colors={_getStateColor().flat()}
    >
      <Container>
        <VectorMap height={450} size {...brMap} layerProps={layerProps} />
        <ColumnWrapper>
          <ScaleContainer>
            <HeatScale colorScale={colorScale} />
          </ScaleContainer>
          <DataBoxContainer>
            <DataBox>
              {clicked ? (
                <React.Fragment>
                  <DataText color="#263465" bold>
                    {dataValues.label}
                  </DataText>
                  {dataValues.obs ? (
                    <DataText color='rgba(0, 0, 0, 0.60)'>{dataValues.obs}</DataText>
                  ) : (
                    <React.Fragment>
                      <br />
                      <DataText color="rgba(0, 0, 0, 0.54)">
                        Total: {dataValues.total}
                      </DataText>
                      <br />
                      <DataText color="#3B873E">
                        No prazo:{' '}
                        {(comparativeUnit === comparativeUnits.VALUE &&
                          `R$${dataValues.onTimeValue}`) ||
                          (comparativeUnit === comparativeUnits.WEIGHT &&
                            `${dataValues.onTimeWeight}t`) ||
                          (comparativeUnit ===
                            comparativeUnits.PERFORMANCE &&
                            `${dataValues.onTimeQty} (${dataValues.onTimePercentage}%)`)}
                      </DataText>
                      <br />
                      <DataText color="#E31B0C">
                        Atrasado:{' '}
                        {(comparativeUnit === comparativeUnits.VALUE &&
                          `R$${dataValues.lateValue}`) ||
                          (comparativeUnit === comparativeUnits.WEIGHT &&
                            `${dataValues.lateWeight}t`) ||
                          (comparativeUnit ===
                            comparativeUnits.PERFORMANCE &&
                            `${dataValues.lateQty} (${dataValues.latePercentage}%)`)}
                      </DataText>
                    </React.Fragment>
                  )}
                </React.Fragment>
              ) : (
                <DataText color="rgba(0, 0, 0, 0.54)">
                  Clique em um estado ou região no mapa ao lado para ver
                  mais detalhes
                </DataText>
              )}
            </DataBox>
          </DataBoxContainer>
          <ObsText>
            Notas com abono de performance entregues dentro do prazo abonado
            são classificadas como <strong>no prazo</strong>.
          </ObsText>
        </ColumnWrapper>
      </Container>
    </MapContainer>
  );
};

export default HeatMap;
